import type { Calendar } from "@/components/calendars/api.ts";
import { calendarQueries, getCalendars } from "@/components/calendars/api.ts";
import type { CalendarEvent } from "@/types/events.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";

/**
 * Look up an event by ID from cached React Query calendar event data.
 *
 * Live: subscribes to the query cache, so when a socket-driven refetch lands a
 * new copy of the event in a `["calendarEvents"]` query, callers re-render with
 * it — an open detail view must converge on an external edit (BIG-6330), not
 * hold the object snapshotted at open time.
 *
 * If the event later vanishes from the cache (a "this and following" split
 * re-creates follow-on occurrences under new ids), keeps returning the last
 * found copy so an open view stays stable instead of flipping to "not found".
 */
export const useEventFromCache = (eventId: string): CalendarEvent | undefined => {
  const queryClient = useQueryClient();
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      queryClient.getQueryCache().subscribe((cacheEvent) => {
        if (cacheEvent.query.queryKey[0] === "calendarEvents") onStoreChange();
      }),
    [queryClient],
  );
  // Snapshot stability: this returns the event object held in cache data —
  // not a fresh allocation — so repeated calls yield the same reference until
  // a cache write actually replaces it, as useSyncExternalStore requires.
  const getSnapshot = useCallback(() => {
    // Newest-fetched range first: invalidation only refetches mounted queries,
    // so an unmounted range can retain an older copy of the same event — a
    // first-match scan in insertion order could keep serving that stale copy.
    const queries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["calendarEvents"] })
      .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
    for (const query of queries) {
      const data = query.state.data as { events?: CalendarEvent[] } | undefined;
      const found = data?.events?.find((e) => e.gcalId === eventId);
      if (found) return found;
    }
    return undefined;
  }, [queryClient, eventId]);
  const event = useSyncExternalStore(subscribe, getSnapshot);

  const lastFoundRef = useRef(event);
  if (event) lastFoundRef.current = event;
  // A different eventId must not inherit the previous event's fallback.
  if (lastFoundRef.current && lastFoundRef.current.gcalId !== eventId) {
    lastFoundRef.current = undefined;
  }
  return event ?? lastFoundRef.current;
};

/**
 * Identity of an event's scheduled time, for use in `EventDetailPanel`'s
 * `key`. The panel seeds its form state at mount (the dirty-check baseline),
 * so a changed fingerprint is how a pristine panel picks up an externally
 * rescheduled time: the parent remounts it on the fresh event.
 */
export const eventTimeFingerprint = (event: CalendarEvent): string =>
  `${event.start.dateTime}|${event.end.dateTime}`;

/**
 * Tracks `value` while `isDirty` is false and holds it frozen while true.
 *
 * Used to gate the callers' key-based panel remount on a pristine form:
 * mid-edit, a cache update must not remount the panel (it would discard the
 * user's draft and shift the dirty-check baseline), so the key segment
 * freezes until the edit resolves.
 */
export const useFrozenWhileDirty = (value: string, isDirty: boolean): string => {
  const frozenRef = useRef(value);
  if (!isDirty) frozenRef.current = value;
  return frozenRef.current;
};

/**
 * The time segment of `EventDetailPanel`'s `key`: the event's scheduled time,
 * frozen while the form is dirty. Appending it to the key makes a pristine
 * panel remount (and so re-seed its form state) when the event is externally
 * rescheduled, while a mid-edit panel keeps its draft and baseline.
 */
export const useEventPanelTimeKey = (event: CalendarEvent | undefined, isDirty: boolean): string =>
  useFrozenWhileDirty(event ? eventTimeFingerprint(event) : "", isDirty);

/**
 * The access role of the calendar an event lives on, or `undefined` while the
 * calendar list loads or when the calendar isn't in it.
 */
export const useCalendarAccessRole = (calendarId: string): string | null | undefined => {
  const { data: calendars } = useQuery(calendarQueries.list());
  return useMemo(
    () => calendars?.find((c) => c.calendarId === calendarId)?.accessRole,
    [calendars, calendarId],
  );
};

export const useCalendarName = (calendarId: string): string | undefined => {
  const { data: calendars } = useQuery<Calendar[]>({
    queryKey: ["calendars"],
    queryFn: getCalendars,
  });
  return useMemo(() => {
    const cal = calendars?.find((c) => c.calendarId === calendarId);
    return cal?.summary ?? undefined;
  }, [calendars, calendarId]);
};
